import React, { useState, useEffect } from 'react';
import './Support.css';
import config from '../config';

const Support = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [replyMessage, setReplyMessage] = useState('');

    // New ticket form
    const [newTicket, setNewTicket] = useState({
        subject: '',
        description: '',
        priority: 'medium'
    });

    useEffect(() => {
        fetchTickets();
    }, []);

    const getCookie = (name) => {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    };

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const csrfToken = getCookie('csrftoken');
            const response = await fetch(`${config.API_BASE_URL}/api/support/tickets/`, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                credentials: 'include',
            });

            if (!response.ok) throw new Error('Failed to fetch tickets');
            const data = await response.json();
            setTickets(data);
        } catch (error) {
            // console.error('Error fetching tickets:', error);
            setError('Failed to load tickets');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTicket = async (e) => {
        e.preventDefault();
        try {
            const csrfToken = getCookie('csrftoken');
            const response = await fetch(`${config.API_BASE_URL}/api/support/tickets/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                credentials: 'include',
                body: JSON.stringify(newTicket),
            });

            if (response.ok) {
                setShowCreateForm(false);
                setNewTicket({ subject: '', description: '', priority: 'medium' });
                fetchTickets();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to create ticket');
            }
        } catch (error) {
            // console.error('Error creating ticket:', error);
            setError('Failed to create ticket');
        }
    };

    const handleAddReply = async (ticketId) => {
        if (!replyMessage.trim()) return;

        try {
            const csrfToken = getCookie('csrftoken');
            const response = await fetch(`${config.API_BASE_URL}/api/support/tickets/${ticketId}/reply/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                credentials: 'include',
                body: JSON.stringify({ message: replyMessage }),
            });

            if (response.ok) {
                setReplyMessage('');
                fetchTickets();
                // Refresh the selected ticket view
                const updatedTicket = tickets.find(t => t.id === ticketId);
                if (updatedTicket) {
                    const detailResponse = await fetch(`${config.API_BASE_URL}/api/support/tickets/${ticketId}/`, {
                        headers: { 'X-CSRFToken': csrfToken },
                        credentials: 'include',
                    });
                    const detailData = await detailResponse.json();
                    setSelectedTicket(detailData);
                }
            }
        } catch (error) {
            // console.error('Error adding reply:', error);
            setError('Failed to add reply');
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'low': return '#10b981';
            case 'medium': return '#f59e0b';
            case 'high': return '#ef4444';
            case 'urgent': return '#dc2626';
            default: return '#6b7280';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return '#3b82f6';
            case 'in_progress': return '#f59e0b';
            case 'resolved': return '#10b981';
            case 'closed': return '#6b7280';
            default: return '#6b7280';
        }
    };

    if (loading) {
        return (
            <div className="support-loading">
                <div className="loading-spinner"></div>
                <p>Loading your tickets...</p>
            </div>
        );
    }

    return (
        <div className="support-container">
            {/* <div className="support-header">
                <button onClick={() => setShowCreateForm(true)} className="create-ticket-btn">
                    + New Ticket
                </button>
            </div> */}

            {error && (
                <div className="support-error">
                    <span>⚠️</span> {error}
                    <button onClick={() => setError('')}>×</button>
                </div>
            )}

            {/* Create Ticket Modal */}
            {showCreateForm && (
                <div className="modal-overlay">
                    <div className="modal-content support-modal">
                        <div className="modal-header">
                            <div className="modal-header-icon">🎫</div>
                            <h2>Create New Support Ticket</h2>
                            <button className="modal-close" onClick={() => setShowCreateForm(false)}>×</button>
                        </div>

                        <form onSubmit={handleCreateTicket} className="ticket-form">
                            <div className="form-group">
                                <label className="form-label">
                                    <span className="label-icon">📝</span>
                                    Subject <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={newTicket.subject}
                                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                                    required
                                    placeholder="e.g., Unable to generate PDF, Template creation issue, Billing question..."
                                />
                                <p className="form-hint">Briefly describe the main issue you're facing</p>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">
                                        <span className="label-icon">⚡</span>
                                        Priority <span className="required">*</span>
                                    </label>
                                    <div className="priority-options">
                                        <label className={`priority-option low ${newTicket.priority === 'low' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="priority"
                                                value="low"
                                                checked={newTicket.priority === 'low'}
                                                onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                                            />
                                            <span className="priority-dot low-dot"></span>
                                            <span className="priority-text">Low</span>
                                            <span className="priority-desc">Minor issue, no urgency</span>
                                        </label>

                                        <label className={`priority-option medium ${newTicket.priority === 'medium' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="priority"
                                                value="medium"
                                                checked={newTicket.priority === 'medium'}
                                                onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                                            />
                                            <span className="priority-dot medium-dot"></span>
                                            <span className="priority-text">Medium</span>
                                            <span className="priority-desc">Normal priority</span>
                                        </label>

                                        <label className={`priority-option high ${newTicket.priority === 'high' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="priority"
                                                value="high"
                                                checked={newTicket.priority === 'high'}
                                                onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                                            />
                                            <span className="priority-dot high-dot"></span>
                                            <span className="priority-text">High</span>
                                            <span className="priority-desc">Urgent, affecting workflow</span>
                                        </label>

                                        <label className={`priority-option urgent ${newTicket.priority === 'urgent' ? 'selected' : ''}`}>
                                            <input
                                                type="radio"
                                                name="priority"
                                                value="urgent"
                                                checked={newTicket.priority === 'urgent'}
                                                onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                                            />
                                            <span className="priority-dot urgent-dot"></span>
                                            <span className="priority-text">Urgent</span>
                                            <span className="priority-desc">Critical, immediate attention needed</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    <span className="label-icon">📄</span>
                                    Description <span className="required">*</span>
                                </label>
                                <textarea
                                    className="form-textarea"
                                    value={newTicket.description}
                                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                                    required
                                    rows={6}
                                    placeholder="Please provide detailed information about your issue...
            
Examples:
- What were you trying to do?
- What error message did you see?
- Steps to reproduce the issue
- Any screenshots or additional context"
                                />
                                <p className="form-hint">Detailed descriptions help us resolve your issue faster</p>
                            </div>

                            <div className="form-divider"></div>

                            <div className="modal-footer">
                                <button type="button" onClick={() => setShowCreateForm(false)} className="cancel-btn">
                                    Cancel
                                </button>
                                <button type="submit" className="submit-btn">
                                    <span className="btn-icon">✉️</span>
                                    Create Ticket
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Ticket List */}
            {selectedTicket ? (
                <div className="ticket-detail-view">
                    <button onClick={() => setSelectedTicket(null)} className="back-btn">
                        ← Back to Tickets
                    </button>

                    <div className="ticket-detail-card">
                        <div className="ticket-detail-header">
                            <h2>{selectedTicket.subject}</h2>
                            <div className="ticket-badges">
                                <span className="priority-badge" style={{ background: getPriorityColor(selectedTicket.priority) }}>
                                    {selectedTicket.priority}
                                </span>
                                <span className="status-badge" style={{ background: getStatusColor(selectedTicket.status) }}>
                                    {selectedTicket.status}
                                </span>
                            </div>
                        </div>
                        <div className="ticket-detail-meta">
                            <span>Ticket #{selectedTicket.id}</span>
                            <span>Created: {selectedTicket.formatted_date}</span>
                        </div>
                        <div className="ticket-description">
                            <h4>Description</h4>
                            <p>{selectedTicket.description}</p>
                        </div>

                        <div className="ticket-replies">
                            <h4>Conversation</h4>
                            {selectedTicket.replies?.map((reply, index) => (
                                <div key={index} className={`reply-item ${reply.is_admin_reply ? 'admin-reply' : 'user-reply'}`}>
                                    <div className="reply-header">
                                        <strong>{reply.user_name}</strong>
                                        <span>{reply.formatted_date}</span>
                                    </div>
                                    <div className="reply-message">{reply.message}</div>
                                </div>
                            ))}

                            <div className="reply-form">
                                <textarea
                                    placeholder="Type your reply here..."
                                    value={replyMessage}
                                    onChange={(e) => setReplyMessage(e.target.value)}
                                    rows={3}
                                />
                                <button onClick={() => handleAddReply(selectedTicket.id)} disabled={!replyMessage.trim()}>
                                    Send Reply
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div>
                    <div className="support-header">
                        <button onClick={() => setShowCreateForm(true)} className="create-ticket-btn">
                            + New Ticket
                        </button>
                    </div>
                    <div className="tickets-list">
                        {tickets.length === 0 ? (
                            <div className="no-tickets">
                                <div className="no-tickets-icon">🎫</div>
                                <h3>No tickets yet</h3>
                                <p>Create your first support ticket and we'll help you ASAP!</p>
                            </div>
                        ) : (
                            tickets.map(ticket => (
                                <div key={ticket.id} className="ticket-card" onClick={() => setSelectedTicket(ticket)}>
                                    <div className="ticket-card-header">
                                        <h3>{ticket.subject}</h3>
                                        <div className="ticket-badges">
                                            <span className="priority-badge" style={{ background: getPriorityColor(ticket.priority) }}>
                                                {ticket.priority}
                                            </span>
                                            <span className="status-badge" style={{ background: getStatusColor(ticket.status) }}>
                                                {ticket.status}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="ticket-description-preview">{ticket.description.substring(0, 100)}...</p>
                                    <div className="ticket-card-footer">
                                        <span>Ticket #{ticket.id}</span>
                                        <span>{ticket.formatted_date}</span>
                                        <span>{ticket.replies?.length || 0} replies</span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Support;